import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, ImagePlus, Sparkles, Loader2, MessageCircle } from "lucide-react";
import api from "../lib/api";
import { CHAPTERS } from "../data/content";
import { Ornament } from "../components/Ornament";
import { VoiceRecorder } from "../components/VoiceRecorder";
import { AudioPlayer } from "../components/AudioPlayer";
import { UpgradeModal } from "../components/UpgradeModal";
import { useTranslation } from "react-i18next";

// Deterministic non-colliding prompt_index for a question request.
// Uses the last 4 chars of the question_id as hex-ish → 1000..1999
function requestPromptIndex(qid) {
  const tail = (qid || "").replace(/[^0-9a-fA-F]/g, "").slice(-6) || "0";
  const n = parseInt(tail, 16) || 0;
  return 1000 + (n % 900);
}

export default function ChapterInterview() {
  const { profileId, chapterId } = useParams();
  const [searchParams] = useSearchParams();
  const questionId = searchParams.get("q");
  const navigate = useNavigate();
  const { t } = useTranslation();
  const chapter = useMemo(() => CHAPTERS.find((c) => c.id === Number(chapterId)), [chapterId]);
  const [profile, setProfile] = useState(null);
  const [me, setMe] = useState(null);
  const [stories, setStories] = useState([]);
  const [promptIndex, setPromptIndex] = useState(0);
  const [answer, setAnswer] = useState("");
  const [photoPath, setPhotoPath] = useState(null);
  const [audioPath, setAudioPath] = useState(null);
  const [followupQ, setFollowupQ] = useState("");
  const [followupA, setFollowupA] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [askingAI, setAskingAI] = useState(false);
  const [aiError, setAIError] = useState("");
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState("");
  const [requestData, setRequestData] = useState(null); // { question_id, question_text, from_name, chapter_id }
  const fileInputRef = useRef();

  useEffect(() => {
    (async () => {
      try {
        const calls = [
          api.get("/profiles"),
          api.get(`/stories/${profileId}`),
          api.get("/auth/me"),
        ];
        if (questionId) calls.push(api.get(`/questions/${profileId}`).catch(() => ({ data: [] })));
        const results = await Promise.all(calls);
        const [plist, slist, meRes, qlistRes] = results;
        const p = (plist.data || []).find((p) => p.profile_id === profileId);
        if (!p || !chapter) { navigate("/dashboard"); return; }
        setProfile(p);
        setStories(slist.data || []);
        setMe(meRes.data);
        if (questionId && qlistRes) {
          const q = (qlistRes.data || []).find((x) => x.question_id === questionId);
          if (q) {
            setRequestData(q);
            setPromptIndex(requestPromptIndex(q.question_id));
          } else {
            // question not found — fall back to normal chapter flow
            navigate(`/app/profile/${profileId}`);
            return;
          }
        }
      } catch (e) { navigate("/dashboard"); } finally { setLoading(false); }
    })();
  }, [profileId, chapterId, questionId, chapter, navigate]);

  useEffect(() => {
    const existing = stories.find(
      (s) => s.chapter_id === Number(chapterId) && s.prompt_index === promptIndex
    );
    setAnswer(existing?.answer_text || "");
    setPhotoPath(existing?.photo_path || null);
    setAudioPath(existing?.audio_path || null);
    setFollowupQ(existing?.followup_question || "");
    setFollowupA(existing?.followup_answer || "");
    setAIError("");
  }, [promptIndex, stories, chapterId]);

  const promptText = requestData ? requestData.question_text : (chapter?.prompts?.[promptIndex] || "");

  const saveStory = async (nextIndex = null) => {
    setSaving(true);
    try {
      const payload = {
        profile_id: profileId,
        chapter_id: Number(chapterId),
        prompt_index: promptIndex,
        answer_text: answer || "",
        followup_question: followupQ || null,
        followup_answer: followupA || null,
        photo_path: photoPath || null,
        audio_path: audioPath || null,
      };
      if (requestData) {
        payload.source_question_id = requestData.question_id;
        payload.custom_prompt = requestData.question_text;
      }
      const res = await api.post("/stories", payload);
      setStories((prev) => {
        const others = prev.filter((s) => !(s.chapter_id === Number(chapterId) && s.prompt_index === promptIndex));
        return [...others, res.data];
      });
      // If we're answering a family request, mark it as answered
      if (requestData && (answer || "").trim().length > 0) {
        try { await api.patch(`/questions/${requestData.question_id}/answered`); } catch (e) { /* ignore */ }
      }
      setSavedFlash(true); setTimeout(() => setSavedFlash(false), 1800);
      if (nextIndex !== null) setPromptIndex(nextIndex);
    } catch (e) { /* ignore */ } finally { setSaving(false); }
  };

  const onTranscribed = (text, audioPathReturned) => {
    if (text) setAnswer((prev) => (prev ? prev.trim() + " " + text : text));
    if (audioPathReturned) setAudioPath(audioPathReturned);
  };

  const askFollowup = async () => {
    setAskingAI(true); setAIError("");
    try {
      const res = await api.post("/ai/followup", {
        profile_id: profileId,
        chapter_id: Number(chapterId),
        prompt_index: promptIndex,
        prompt_text: promptText,
        answer_text: answer,
      });
      setFollowupQ(res.data?.question || "");
      // Update local me.ai_followups_used counter
      if (typeof res.data?.followups_used === "number") {
        setMe((m) => m ? { ...m, ai_followups_used: res.data.followups_used } : m);
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 402 && typeof d === "object" && d?.code === "ai_limit") {
        setUpgradeReason("ai_limit"); setUpgradeOpen(true);
      } else {
        setAIError(typeof d === "string" ? d : "Couldn't draw out a follow-up just now. Try again in a moment.");
      }
    } finally { setAskingAI(false); }
  };

  const onUploadPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const res = await api.post("/files/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoPath(res.data?.path || null);
    } catch (e2) {
      if (e2?.response?.status === 402) {
        setUpgradeReason("photo_locked"); setUpgradeOpen(true);
      }
    }
  };

  const isFree = (me?.subscription_tier || "free") === "free";
  const requestPhoto = () => {
    if (isFree) { setUpgradeReason("photo_locked"); setUpgradeOpen(true); return; }
    fileInputRef.current?.click();
  };

  if (loading || !chapter || !profile || !me) {
    return (
      <div className="min-h-screen flex items-center justify-center paper">
        <p className="italic text-[#8C8278]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.3rem" }}>
          Reflecting on your story…
        </p>
      </div>
    );
  }

  const total = chapter.prompts.length;
  const wordCount = (answer || "").trim().split(/\s+/).filter(Boolean).length;
  const canAskAI = wordCount >= 8;
  const aiLimit = me.ai_followups_limit;
  const aiUsed = me.ai_followups_used || 0;
  const aiUsedUp = aiLimit != null && aiUsed >= aiLimit;

  return (
    <div className="min-h-screen paper" data-testid="chapter-interview-page">
      <header className="max-w-3xl mx-auto px-6 sm:px-10 py-8 flex items-center justify-between">
        <Link to={`/app/profile/${profileId}`} className="flex items-center gap-2 label-ui text-[11px] text-[#8C8278] hover:text-[#111D33] transition-colors" data-testid="back-to-chapters">
          <ChevronLeft size={16} /> {requestData ? t("common.back", "Back") : "Chapters"}
        </Link>
        <div className="text-center">
          {requestData ? (
            <>
              <p className="chapter-roman text-[#B8973A] tracking-[0.25em]" data-testid="request-mode-label">A FAMILY REQUEST</p>
              <h1 className="text-2xl" style={{ fontFamily: "Cormorant Garamond, serif" }}>{profile.name}</h1>
            </>
          ) : (
            <>
              <p className="chapter-roman" data-testid="chapter-roman">{chapter.roman}</p>
              <h1 className="text-2xl" style={{ fontFamily: "Cormorant Garamond, serif" }}>{chapter.title}</h1>
            </>
          )}
        </div>
        {requestData ? (
          <span className="label-ui text-[10px] text-[#B8973A]" data-testid="request-from-badge">
            — {requestData.from_name || "Your family"}
          </span>
        ) : (
          <span className="label-ui text-[10px] text-[#8C8278]" data-testid="prompt-progress">{promptIndex + 1} of {total}</span>
        )}
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-10 py-6">
        {!requestData && (
          <div className="h-px w-full bg-[#F0EAE0] mb-16">
            <div className="h-px bg-[#B8973A] transition-all duration-500" style={{ width: `${((promptIndex + 1) / total) * 100}%` }} />
          </div>
        )}

        {requestData && (
          <div className="mb-10 p-5 bg-[#F7F0E0] flex items-start gap-3" style={{ border: "1px solid #E8D5A3", borderRadius: 4 }} data-testid="request-context-banner">
            <MessageCircle size={16} className="text-[#B8973A] mt-1 shrink-0" />
            <div>
              <p className="label-ui text-[10px] text-[#B8973A] mb-1 tracking-[0.25em]">REQUESTED BY {(requestData.from_name || "your family").toUpperCase()}</p>
              <p className="text-[#1B2A4A] italic" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.05rem", lineHeight: 1.5 }}>
                Answer in your own words — voice, typed, or both. Your family is listening.
              </p>
            </div>
          </div>
        )}

        <p className="text-center mb-12 text-[#111D33]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "clamp(1.4rem, 3.5vw, 2rem)", fontWeight: 400, lineHeight: 1.5 }} data-testid="prompt-text">
          {promptText}
        </p>

        <div className="bg-white hairline-cream p-6 sm:p-8 mb-6" style={{ borderRadius: 4 }}>
          <textarea
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            placeholder="Write here… or tap the microphone below to speak."
            className="w-full min-h-[220px] bg-transparent resize-y focus:outline-none placeholder:text-[#8C8278]"
            style={{ fontFamily: "Georgia, serif", fontSize: "1.05rem", lineHeight: 1.85, color: "#1B2A4A" }}
            data-testid="answer-textarea"
          />
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-6">
            {isFree ? (
              <button
                type="button"
                onClick={() => { setUpgradeReason("voice_locked"); setUpgradeOpen(true); }}
                className="flex flex-col items-center gap-3"
                data-testid="voice-record-button-locked"
              >
                <span
                  className="flex items-center justify-center hairline"
                  style={{ width: 76, height: 76, borderRadius: 999, background: "#FFFFFF", color: "#111D33", borderColor: "#E8D5A3" }}
                >
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
                </span>
                <span className="label-ui text-[11px] text-[#B8973A]">Subscribe to record voice</span>
              </button>
            ) : (
              <VoiceRecorder onTranscribed={onTranscribed} />
            )}
            <div className="flex flex-wrap gap-3 justify-center">
              <input type="file" ref={fileInputRef} accept="image/*" onChange={onUploadPhoto} className="hidden" data-testid="photo-file-input" />
              <button onClick={requestPhoto} className="btn-outline-navy flex items-center gap-2" data-testid="add-photo-btn">
                <ImagePlus size={16} /> {photoPath ? "Replace photo" : "Add a photo"}
              </button>
              <button
                onClick={aiUsedUp ? () => { setUpgradeReason("ai_limit"); setUpgradeOpen(true); } : askFollowup}
                disabled={(!canAskAI && !aiUsedUp) || askingAI}
                className="btn-gold flex items-center gap-2"
                data-testid="ai-followup-btn"
              >
                {askingAI ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                Ask a follow-up
              </button>
            </div>
          </div>
          {!canAskAI && !aiUsedUp && (
            <p className="text-xs text-[#8C8278] italic mt-4 text-center" data-testid="ai-min-words-hint">
              Write a sentence or two and we&apos;ll gently draw out more.
            </p>
          )}
          {aiLimit != null && (
            <p className="text-xs text-[#8C8278] italic mt-3 text-center" data-testid="ai-quota">
              {aiUsedUp ? "You've used your free follow-ups. " : `Free follow-ups left: ${Math.max(0, aiLimit - aiUsed)} of ${aiLimit}. `}
              <button onClick={() => navigate("/subscribe")} className="text-[#B8973A] underline-offset-4 hover:underline" data-testid="ai-quota-upgrade-link">
                Subscribe for unlimited
              </button>
            </p>
          )}
          {aiError && <p className="text-sm text-red-700 mt-3" data-testid="ai-followup-error">{aiError}</p>}
        </div>

        {photoPath && <PhotoPreview path={photoPath} />}
        {audioPath && (
          <div className="mb-6">
            <AudioPlayer path={audioPath} label="Their voice — saved with this story." />
          </div>
        )}

        {followupQ && (
          <div className="p-6 sm:p-8 mb-6 animate-fade-up" style={{ background: "#F7F0E0", border: "1px solid #B8973A", borderRadius: 4 }} data-testid="followup-block">
            <p className="label-ui text-[10px] text-[#B8973A] mb-3">A gentle follow-up</p>
            <p className="text-[#1B2A4A] mb-5" style={{ fontFamily: "Cormorant Garamond, serif", fontStyle: "italic", fontSize: "1.35rem", lineHeight: 1.5 }} data-testid="followup-question">
              {followupQ}
            </p>
            <textarea
              value={followupA} onChange={(e) => setFollowupA(e.target.value)}
              placeholder="Take your time…"
              className="w-full min-h-[120px] bg-white hairline-cream p-4 focus:outline-none resize-y"
              style={{ fontFamily: "Georgia, serif", fontSize: "1rem", lineHeight: 1.8, color: "#1B2A4A", borderRadius: 4 }}
              data-testid="followup-answer-textarea"
            />
          </div>
        )}

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-10">
          {requestData ? (
            <>
              <button onClick={() => navigate(`/app/profile/${profileId}`)} className="btn-outline-navy flex items-center gap-2" data-testid="request-cancel-btn">
                <ChevronLeft size={16} /> {t("common.back", "Back")}
              </button>
              <p className="label-ui text-[10px] text-[#8C8278]" data-testid="saved-flash">
                {savedFlash ? "Beautifully saved. Every conversation becomes someone else's beginning." : "Answering a family request — take your time."}
              </p>
              <button
                onClick={() => {
                  saveStory(null);
                  setTimeout(() => navigate(`/app/profile/${profileId}`), 700);
                }}
                disabled={saving || !(answer || "").trim()}
                className="btn-primary flex items-center gap-2 disabled:opacity-40"
                data-testid="request-save-btn"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : null}
                Save answer
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => promptIndex > 0 && setPromptIndex(promptIndex - 1)}
                disabled={promptIndex === 0}
                className="btn-outline-navy flex items-center gap-2 disabled:opacity-30"
                data-testid="prev-prompt-btn"
              >
                <ChevronLeft size={16} /> Previous
              </button>
              <p className="label-ui text-[10px] text-[#8C8278]" data-testid="saved-flash">
                {savedFlash ? "Beautifully saved. Every conversation becomes someone else's beginning." : "You can always come back and add more — memories surface when they're ready."}
              </p>
              <div className="flex gap-3">
                <button onClick={() => promptIndex < total - 1 ? setPromptIndex(promptIndex + 1) : navigate(`/app/profile/${profileId}`)} className="btn-outline-navy" data-testid="skip-btn">
                  Skip for now
                </button>
                <button
                  onClick={() => {
                    const isLast = promptIndex >= total - 1;
                    saveStory(isLast ? null : promptIndex + 1);
                    if (isLast) setTimeout(() => navigate(`/app/profile/${profileId}`), 600);
                  }}
                  disabled={saving}
                  className="btn-primary flex items-center gap-2"
                  data-testid="save-continue-btn"
                >
                  {saving ? <Loader2 size={16} className="animate-spin" /> : null}
                  {promptIndex >= total - 1 ? "Save & finish" : "Save & continue"}
                  {promptIndex < total - 1 && <ChevronRight size={16} />}
                </button>
              </div>
            </>
          )}
        </div>

        <Ornament className="my-16" />
      </main>

      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} reason={upgradeReason} />
    </div>
  );
}

function PhotoPreview({ path }) {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    let url;
    (async () => {
      try {
        const res = await api.get(`/files/${path}`, { responseType: "blob" });
        url = URL.createObjectURL(res.data);
        setBlobUrl(url);
      } catch (e) { /* ignore */ }
    })();
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [path]);
  if (!blobUrl) return null;
  return (
    <div className="mb-6" data-testid="photo-preview">
      <img src={blobUrl} alt="Story attachment" className="w-full max-h-[400px] object-cover hairline-cream" style={{ borderRadius: 4 }} />
    </div>
  );
}
